import React, { useState, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import GridLight from "highcharts/themes/grid-light";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import ChartFallbackImg from "./ChartFallbackImg";

HighchartsMore(Highcharts); // includes packedbubble internally
GridLight(Highcharts);

const PackedBubbleChart = ({ langdata }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const chartRef = useRef(null);

  // Enhanced color palette with gradients and corresponding label colors
  const colorScheme = [
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#FF6B6B"],
          [1, "#ee5253"],
        ],
      },
      label: "#ee5253",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#4ECDC4"],
          [1, "#45B7AF"],
        ],
      },
      label: "#45B7AF",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#6C5CE7"],
          [1, "#5B4BC9"],
        ],
      },
      label: "#5B4BC9",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#A8E6CF"],
          [1, "#94D3BC"],
        ],
      },
      label: "#94D3BC",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#FFB6B9"],
          [1, "#E8A0A3"],
        ],
      },
      label: "#E8A0A3",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#957DAD"],
          [1, "#806A96"],
        ],
      },
      label: "#806A96",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#FED766"],
          [1, "#E5C25C"],
        ],
      },
      label: "#E5C25C",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#2AB7CA"],
          [1, "#25A4B5"],
        ],
      },
      label: "#25A4B5",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#F86624"],
          [1, "#E05A20"],
        ],
      },
      label: "#E05A20",
    },
    {
      bubble: {
        radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
        stops: [
          [0, "#45B7D1"],
          [1, "#3EA4BB"],
        ],
      },
      label: "#3EA4BB",
    },
  ];

  // Memoize series data to prevent unnecessary re-renders
  const seriesData = React.useMemo(() => {
    if (!langdata || typeof langdata !== "object") {
      return [];
    }

    return Object.entries(langdata)
      .filter(([name, value]) => name.trim() !== "" && value > 0)
      .map(([name, value], index) => {
        const colorIndex = index % colorScheme.length;
        return {
          name: name,
          color: colorScheme[colorIndex].label,
          data: [
            {
              name: name,
              value: value,
              color: colorScheme[colorIndex].bubble,
            },
          ],
        };
      });
  }, [langdata]);

  const handleBubbleClick = (event) => {
    const clickedLanguage = event.point.name;
    setSelectedLanguage(clickedLanguage);
    setIsDrawerOpen(true);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current?.chart) {
        try {
          chartRef.current.chart.destroy();
        } catch (error) {
          console.warn("Chart cleanup error:", error);
        }
      }
    };
  }, []);

  const options = {
    chart: {
      type: "packedbubble",
      height: "400",
      backgroundColor: "#ffffff",
      animation: {
        duration: 1000,
      },
    },
    title: {
      text: "Language Breakdown",
      
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      packedbubble: {
        minSize: "30%",
        maxSize: "100%",
        zMin: 0,
        zMax: Math.max(...Object.values(langdata || {}), 100),
        layoutAlgorithm: {
          splitSeries: false,
          gravitationalConstant: 0.02,
          seriesInteraction: true,
          dragBetweenSeries: true,
          parentNodeLimit: true,
          // Add these properties to help with layout stability
          // friction: 0.9,
          linkLength: 30,
          initialPositions: "circle",
        },
        dataLabels: {
          enabled: true,
          format: "{point.name}",
          style: {
            fontSize: "12px",
            fontWeight: "bold",
            color: "#333",
            textOutline: "none",
          },
          filter: {
            property: "value",
            operator: ">",
            value: 0,
          },
        },
        point: {
          events: {
            click: handleBubbleClick,
          },
        },
        // Add animation settings
        animation: {
          duration: 1000,
        },
        // Add states for better interaction
        states: {
          hover: {
            brightness: 0.1,
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      pointFormat: "<b>{point.name}</b><br>Count: {point.value}",
      followPointer: true,
    },
    series: seriesData,
  };

  // Don't render if no data
  if (!langdata || Object.keys(langdata).length === 0) {
    return (
      <div style={{ marginTop: "-30px" }}>
        <ChartFallbackImg titleText="Language Distribution" />
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: "8px",
        background: "white",
      }}
    >
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        // Add these props to help with stability
        immutable={false}
        updateArgs={[true, true, true]}
      />
      <SwipeableDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)}
        PaperProps={{
          sx: {
            height: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            width: 650,
            padding: 3,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {selectedLanguage && <ChartDatadrawer language={selectedLanguage} />}
        </Box>
      </SwipeableDrawer>
    </div>
  );
};

export default PackedBubbleChart;
